import { Chip, Stack, Tooltip } from '@mui/material';

import {
  incomingLabel,
  listingLabel,
  moveOutLabel,
  renewalLabel,
  type ResolvedUnitStatus,
  type UnitStatusView,
} from '@/helpers/unitStatus';

/**
 * Renders a unit's live status as a row of chips.
 *
 * Deliberately built from the structured fields rather than `status.label` —
 * the overlays need to be visually distinct (a pending move-out reads very
 * differently from a signed incoming lease), and the label's dates are
 * unlocalized `M/D`.
 */

/**
 * Solid fill + white bold text, matching the "Renewing" / "No Process" chips on
 * the Renewal Status tab (`ExpiringLeaseItem`) and the category chips on
 * `LeaseCard`.
 *
 * Colors MUST go through inline `style`, not `sx`. The theme
 * (`src/mui/mui-reset.tsx`) forces `background: #FFF` on `.MuiChip-colorDefault`
 * — a two-class selector that outranks the single generated class `sx` emits, so
 * an `sx` background silently loses and the white label disappears against it.
 * Inline styles beat any stylesheet rule, which is why every working chip in
 * this app is written this way. Shape and height are left to the theme so these
 * sit consistently with the rest of the UI.
 *
 * "Occupied" is on nearly every row, so it gets a calm blue-grey; the exceptions
 * are what should draw the eye.
 */
type Tone = 'occupied' | 'vacant' | 'renewal' | 'notice' | 'listing' | 'incoming';

const TONES: Record<Tone, string> = {
  occupied: '#546e7a',
  vacant: '#757575',
  renewal: '#2196f3',
  notice: '#ef6c00',
  listing: '#8e24aa',
  incoming: '#00897b',
};

type StatusChipProps = {
  label: string;
  tone: Tone;
  title?: string;
};

function StatusChip({ label, tone, title }: StatusChipProps) {
  const chip = (
    <Chip
      label={label}
      size="small"
      style={{
        backgroundColor: TONES[tone],
        color: 'white',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      }}
    />
  );
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
}

export type UnitStatusBadgeProps = {
  status: UnitStatusView | ResolvedUnitStatus;
  /** Show the "Vacant" chip. Off for views that already group by occupancy. */
  showOccupancy?: boolean;
};

/**
 * Whether this status has anything to draw.
 *
 * Occupied-with-nothing-in-flight is the default state of a building and renders
 * no chips at all, so containers need to know that up front rather than laying
 * out an empty badge.
 */
export function hasStatusChips(
  status: UnitStatusView | ResolvedUnitStatus,
  showOccupancy = true
): boolean {
  const { occupancy, current, incoming, listing } = status;
  return (
    (showOccupancy && occupancy === 'vacant') ||
    !!current?.renewed ||
    !!current?.renewal ||
    !!current?.moveOut ||
    !!listing ||
    !!(incoming?.lease || incoming?.moveIn)
  );
}

export function UnitStatusBadge({ status, showOccupancy = true }: UnitStatusBadgeProps) {
  const { occupancy, current, incoming } = status;

  // Wording is shared with the spreadsheet export so the two can't drift.
  const renewal = renewalLabel(status);
  const moveOut = moveOutLabel(status);
  const listing = listingLabel(status);
  const incomingText = incomingLabel(status);

  // `occupancySource` is only present on a resolved status; when occupancy was
  // inferred from the lease rather than a recorded move event, say so on hover
  // so nobody reads it as a fact the workflows asserted.
  const source = (status as ResolvedUnitStatus).occupancySource;
  const occupancyTitle =
    source === 'inferred'
      ? 'Inferred from the lease — no move-in/move-out event recorded for this unit'
      : undefined;

  if (!hasStatusChips(status, showOccupancy)) return null;

  // nowrap: these sit in fixed-height table rows, and wrapping to a second line
  // pushed the rest of the row out of alignment.
  return (
    <Stack direction="row" gap="4px" alignItems="center" flexWrap="nowrap">
      {/*
        Occupied is the default state of an apartment, so it earns no chip —
        showing it on nearly every row was pure noise. Vacant is the exception
        worth flagging.
      */}
      {showOccupancy && occupancy === 'vacant' && (
        <StatusChip label="Vacant" tone="vacant" title={occupancyTitle} />
      )}

      {renewal && <StatusChip label={renewal} tone="renewal" />}

      {moveOut && (
        <StatusChip
          label={moveOut}
          tone="notice"
          title={current?.tenantName ? `${current.tenantName} gave notice` : undefined}
        />
      )}

      {listing && <StatusChip label={listing} tone="listing" />}

      {incomingText && (
        <StatusChip
          label={incomingText}
          tone="incoming"
          title={incoming?.tenantName ? `Incoming: ${incoming.tenantName}` : undefined}
        />
      )}
    </Stack>
  );
}
